import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2, Power, PowerOff, MoreVertical } from 'lucide-react';
import { User } from '../../types/api';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../services/api';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState<(() => void) | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  
  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();
  
  // Confirmation dialog helper
  const showConfirmation = (message: string, onConfirm: () => void) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => onConfirm);
    setShowConfirmDialog(true);
  };
  
  const handleConfirmAction = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setShowConfirmDialog(false);
    setConfirmDialogAction(null);
  };
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    gender: '',
    address: '',
    dateOfBirth: '',
    occupation: '',
    role: '',
    createdBy: currentUser?.email || 'admin'
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    if (openDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const fetchData = async () => {
    try {
      const [usersData, rolesData] = await Promise.all([
        userAPI.getAll(),
        userAPI.getAvailableRoles()
      ]);
      setUsers(usersData);
      setAvailableRoles(rolesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      gender: '',
      address: '',
      dateOfBirth: '',
      occupation: '',
      role: '',
      createdBy: currentUser?.email || 'admin'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await userAPI.register(formData);
      setShowModal(false);
      
      // Show success message (password always sent via email)
      success(`✓ User created! Login credentials have been emailed to ${formData.email}`);
      
      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        gender: '',
        address: '',
        dateOfBirth: '',
        occupation: '',
        role: '',
        createdBy: currentUser?.email || 'admin'
      });
      
      fetchData();
    } catch (err: any) {
      console.error('Error creating user:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create user. Please check the form and try again.';
      error(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    showConfirmation(
      'Are you sure you want to delete this user?',
      async () => {
        try {
          await userAPI.delete(id);
          success('User deleted successfully!');
          fetchData();
        } catch (err) {
          console.error('Error deleting user:', err);
          error('Failed to delete user');
        }
      }
    );
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    showConfirmation(
      `Are you sure you want to ${action} this user? ${!currentStatus ? 'They will be able to login again.' : 'They will no longer be able to access the system.'}`,
      async () => {
        try {
          await userAPI.toggleActive(id);
          success(`User ${action}d successfully!`);
          fetchData();
        } catch (err: any) {
          console.error(`Error ${action}ing user:`, err);
          const errorMessage = err.response?.data?.message || `Failed to ${action} user`;
          error(errorMessage);
        }
      }
    );
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1 className="header-title">Users</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          <UserPlus size={20} style={{ marginRight: '0.5rem' }} />
          Add User
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Gender</th>
                  <th>Occupation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ opacity: user.isActive === false ? 0.6 : 1 }}>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>{user.gender}</td>
                    <td>{user.occupation}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: user.isActive === false ? '#FEE2E2' : '#DEF7EC',
                        color: user.isActive === false ? '#991B1B' : '#03543F'
                      }}>
                        {user.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div style={{ position: 'relative' }}>
                        <button 
                          className="btn btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === user.id ? null : user.id);
                          }}
                          style={{ padding: '0.25rem 0.5rem' }}
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                        
                        {openDropdown === user.id && (
                          <div 
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: '0.25rem',
                              backgroundColor: 'white',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px',
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                              zIndex: 1000,
                              minWidth: '180px',
                              overflow: 'hidden'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {user.id !== currentUser?.id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleActive(user.id, user.isActive !== false);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 1rem',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  color: user.isActive === false ? '#059669' : '#F59E0B'
                                }}
                              >
                                {user.isActive === false ? <Power size={16} /> : <PowerOff size={16} />}
                                {user.isActive === false ? 'Activate User' : 'Deactivate User'}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(null);
                                // TODO: Implement edit functionality
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem 1rem',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer'
                              }}
                            >
                              <Edit size={16} />
                              Edit User
                            </button>
                            {user.id !== currentUser?.id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(user.id);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 1rem',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  color: '#DC2626'
                                }}
                              >
                                <Trash2 size={16} />
                                Delete User
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add User</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
                <small style={{ color: '#10B981', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block', fontWeight: '500' }}>
                  ✓ A secure password will be auto-generated and emailed to this address.
                </small>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Gender</label>
                  <select
                    className="form-input"
                    value={formData.gender}
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-input"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    required
                  >
                    <option value="">Select Role</option>
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Occupation</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.occupation}
                  onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Confirm Action</h2>
              <button className="modal-close" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.5' }}>{confirmDialogMessage}</p>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Users;
